import type {ApiService} from "~/services/ApiService";
import type {TenantConfiguration, UserSetting} from "~~/api/models";
import {normalizeTenantId} from '~/utils/tenantId';

type OrganisationScopedUserSetting = UserSetting & {
  organisationScoped?: boolean;
};

export class ConfigurationService {
  private apiService: ApiService;

  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }

  private resolveTenantId(tenantId: string | null = this.apiService.activeTenant || null): string {
    const normalizedTenantId = normalizeTenantId(tenantId);

    return normalizedTenantId || normalizeTenantId(this.apiService.resolveActiveTenant()) || '';
  }

  async getConfigurations(keys: string[], tenantId: string | null = this.apiService.activeTenant || null): Promise<TenantConfiguration[]> {
    const resolvedTenantId = this.resolveTenantId(tenantId);

    return this.apiService.get<TenantConfiguration[]>(`api/configurations`, {
      headers: {
        'X-TENANT-ID': resolvedTenantId,
      },
      query: {
        keys
      }
    })
  }

  async getUserSettings(keys: string[], tenantId: string | null = this.apiService.activeTenant || null, organisationScoped: boolean = false): Promise<UserSetting[]> {
    const resolvedTenantId = organisationScoped ? '' : this.resolveTenantId(tenantId);

    return this.apiService.get<UserSetting[]>(`api/settings`, {
      ...(resolvedTenantId ? {
        headers: {
          'X-TENANT-ID': resolvedTenantId,
        }
      } : {}),
      query: {
        keys,
        organisationScoped
      }
    })
  }
  async saveUserSetting(userSetting: UserSetting, tenantId: string | null = this.apiService.activeTenant || null): Promise<UserSetting> {
    const organisationScoped = Boolean((userSetting as OrganisationScopedUserSetting).organisationScoped);
    const resolvedTenantId = organisationScoped ? '' : this.resolveTenantId(tenantId);

    return this.apiService.post<UserSetting>(`api/settings`, {
      ...(resolvedTenantId ? {
        headers: {
          'X-TENANT-ID': resolvedTenantId
        }
      } : {}),
      body: userSetting
    })
  }
}